import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  DEFAULT_PROMO_PAGINATION_PARAMS,
  FOREMAN_PROMOCODES_PAGE_SIZE,
  ForemanPromocode,
  ForemanPromocodesPagination,
} from '@my-account/const';
import { ForemanPromocodesService } from '@my-account/feature';
import { SdvorCustomPaginationComponent } from '@sdvor/shared/ui';
import { ModalTooltipService, SdvorIconComponent } from '@sdvor/shared/ui/v2';
import { CustomPaginationModel } from '@shared/models';
import { BrowserNavigatorService } from '@shared/services';
import { IconTypes } from '@shared/v2';
import { Subject, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CopiedPromocodeTooltipComponent } from './copied-promocode-tooltip';

@Component({
  selector: 'sd-promocode-list',
  imports: [DatePipe, SdvorCustomPaginationComponent, SdvorIconComponent],
  templateUrl: './promocode-list.component.html',
  styleUrl: './promocode-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromocodeListComponent implements OnInit, OnDestroy {
  private readonly foremanPromocodesService = inject(ForemanPromocodesService);
  private readonly browserNavigatorService = inject(BrowserNavigatorService);
  private readonly modalTooltipService = inject(ModalTooltipService);

  /** Запрос на копирование промокода в буфер обмена */
  private readonly copyRequest$ = new Subject<string>();

  private readonly paginationInfo: Signal<ForemanPromocodesPagination | undefined> = toSignal(
    this.foremanPromocodesService.getPromocodesPagination(),
  );

  protected readonly pageSize = FOREMAN_PROMOCODES_PAGE_SIZE;

  protected readonly iconTypes = IconTypes;

  protected readonly promocodes: Signal<ForemanPromocode[]> = toSignal(this.foremanPromocodesService.getPromocodes(), {
    initialValue: [],
  });

  protected readonly loading: Signal<boolean> = toSignal(this.foremanPromocodesService.getPromocodesLoading(), {
    initialValue: false,
  });

  protected readonly error: Signal<boolean> = toSignal(this.foremanPromocodesService.getPromocodesError(), {
    initialValue: false,
  });

  /** Данные для пагинации в формате компонента `sd-pagination` */
  protected readonly pagination = computed<CustomPaginationModel>(() => {
    const pagination = this.paginationInfo();

    if (!pagination) {
      return {};
    }

    return {
      totalCount: pagination.totalItems,
      totalPages: Math.ceil(pagination.totalItems / FOREMAN_PROMOCODES_PAGE_SIZE),
      currentPage: Math.floor(pagination.startIndex / FOREMAN_PROMOCODES_PAGE_SIZE),
    };
  });

  constructor() {
    this.copyRequest$
      .pipe(
        switchMap((name) =>
          this.browserNavigatorService
            .copyToClipboard(name)
            .pipe(tap(() => this.modalTooltipService.open(CopiedPromocodeTooltipComponent))),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.loadPromocodes(0);
  }

  ngOnDestroy(): void {
    this.foremanPromocodesService.resetPromocodes();
  }

  protected changePage(page: number): void {
    this.loadPromocodes(page);
  }

  /** Копирование промокода в буфер обмена */
  protected copyPromocode(promocode: ForemanPromocode): void {
    this.copyRequest$.next(promocode.name);
  }

  private loadPromocodes(page: number): void {
    this.foremanPromocodesService.loadPromocodes({
      ...DEFAULT_PROMO_PAGINATION_PARAMS,
      startIndex: page * FOREMAN_PROMOCODES_PAGE_SIZE,
    });
  }
}
